import React, {
  ComponentProps,
  memo,
  ReactElement,
  ReactNode,
  useMemo,
  useState,
} from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { useHandler } from '@surglogs-app/hooks'
import { PropsWithTheme } from '@surglogs-app/ui.theme'

import { StyleProps } from '../styleProps/StylePropsService'

import { Box } from './Box'
import { FlexBox } from './FlexBox'

type SegmentControllerSize = 'mini' | 'default'

interface ButtonStyleProps extends StyleProps {
  active?: boolean
  disabled?: boolean
  size: SegmentControllerSize
}

const getSizeStyles = ({ theme, size }: PropsWithTheme<ButtonStyleProps>) => {
  if (size === 'mini') {
    return css`
      font-size: ${theme.ui.typography.fontSizes.xxs};
      line-height: ${theme.ui.typography.lineHeights.xxs};
      height: ${theme.ui.spacings['abs.28']};
    `
  }

  return css`
    font-size: ${theme.ui.typography.fontSizes.xs};
    line-height: ${theme.ui.typography.lineHeights.xs};
    height: ${theme.ui.spacings['abs.40']};
  `
}

const SegmentsContainer = styled(FlexBox)<{ disabled?: boolean }>`
  align-items: center;
  justify-content: stretch;
  width: 100%;
  flex-grow: 1;
  background-color: ${({ theme }) => theme.ui.colors['navy.10']};
  border-radius: ${({ theme }) => theme.ui.spacings.sm};
  overflow: hidden;
  padding: ${({ theme }) => theme.ui.spacings.xxs};
  gap: ${({ theme }) => theme.ui.spacings.xs};
`

const SegmentTab = styled(Box)<ButtonStyleProps>`
  background-color: ${({ theme }) => theme.ui.colors['white.100']};
  font-weight: ${({ theme }) => theme.ui.typography.fontWeights.semiBold};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.ui.spacings.xs};
  font-family: ${({ theme }) => theme.ui.typography.fontFamilies.default};

  ${({ active }) =>
    !active &&
    css`
      background-color: transparent;

      &:hover {
        transition: background-color 250ms ease-out;
        background: rgba(255, 255, 255, 0.25);
      }
    `};

  &:disabled {
    cursor: auto;
    user-select: none;
    background-color: ${({ theme, active }) =>
      theme.ui.colors[active ? 'transparent.50' : 'navy.10']};
    color: ${({ theme }) => theme.ui.colors['navy.50']};
  }

  &:focus {
    box-shadow: none;
  }

  ${getSizeStyles}
`

export type SegmentControllerTab = Omit<
  ComponentProps<typeof SegmentTab>,
  'size' | 'disabled'
>

interface SegmentControllerProps
  extends Omit<ComponentProps<typeof FlexBox>, 'children'> {
  tabs: SegmentControllerTab[]
  defaultActiveTabIndex?: number
  children?: ({ activeTabIndex }: { activeTabIndex: number }) => ReactElement
  disabled?: boolean
  headerChildren?: ReactNode
  size?: SegmentControllerSize
}

export const SegmentController = memo(
  ({
    tabs,
    children,
    defaultActiveTabIndex = 0,
    disabled,
    headerChildren,
    size = 'default',
    ...rest
  }: SegmentControllerProps) => {
    const [activeIndex, setActiveIndex] = useState(defaultActiveTabIndex)

    const createTabClickHandler = useHandler((index, onClick) => () => {
      setActiveIndex(index)
      onClick?.()
    })

    const content = useMemo(
      () =>
        tabs.map(
          ({ children: buttonChildren, onClick, ...tabProps }, index) => {
            return (
              <SegmentTab
                key={index}
                {...{
                  as: 'button',
                  type: 'button',
                  onClick: createTabClickHandler(index, onClick),
                  active: index === activeIndex,
                  bra: 'abs.6',
                  bd: 'none',
                  flex: 1,
                  color: 'navy.100',
                  cursor: 'pointer',
                  size,
                  disabled,
                  ...tabProps,
                }}>
                {buttonChildren}
              </SegmentTab>
            )
          },
        ),
      [tabs, createTabClickHandler, activeIndex, size, disabled],
    )

    return (
      <FlexBox {...{ gap: 'sm', fd: 'column', ...rest }}>
        <FlexBox {...{ ai: 'center' }}>
          {headerChildren}
          <SegmentsContainer {...{ disabled }}>{content}</SegmentsContainer>
        </FlexBox>
        {children?.({ activeTabIndex: activeIndex })}
      </FlexBox>
    )
  },
)
